import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ProviderAgreementModalProps {
  /** 是否显示弹窗 */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认/已阅读回调（可选） */
  onAgree?: () => void;
  /** 堆叠层级 (默认 10000) */
  zIndex?: number;
}

export default function ProviderAgreementModal({
  open,
  onClose,
  onAgree,
  zIndex = 10000,
}: ProviderAgreementModalProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // 动画过渡处理
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimating(true);
        });
      });
      // 延迟检查容器内容高度（如内容不足以产生滚动条则直接设为 true）
      setTimeout(() => {
        if (contentRef.current) {
          const { clientHeight, scrollHeight } = contentRef.current;
          if (scrollHeight <= clientHeight + 20) {
            setHasScrolledToBottom(true);
          }
        }
      }, 150);
    } else {
      setAnimating(false);
      timer = setTimeout(() => {
        setVisible(false);
      }, 250);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open]);

  // 监听正文滚动到底部
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (hasScrolledToBottom) return;
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 35) {
      setHasScrolledToBottom(true);
    }
  };

  // ESC 键盘监听
  useEffect(() => {
    if (!open || !visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, visible, onClose]);

  if (!visible) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onAgree) {
      onAgree();
    }
    onClose();
  };

  return createPortal(
    <div
      style={{ zIndex }}
      onClick={handleOverlayClick}
      className={`fixed inset-0 flex items-center justify-center p-4 md:p-6 transition-all duration-300 ease-out select-none ${
        animating
          ? 'bg-black/60 backdrop-blur-md opacity-100'
          : 'bg-black/0 backdrop-blur-none opacity-0 pointer-events-none'
      }`}
    >
      <div
        className={`relative flex flex-col w-full max-w-4xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 transition-all duration-300 ease-out transform ${
          animating ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'
        }`}
      >
        {/* 头部标题栏 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/80 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
              <i className="ri-file-text-line text-lg"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground-950 font-heading">AI创作服务供给方入驻及需求竞标服务协议_草案</h2>
              <p className="text-[11px] text-foreground-500">供需服务模块 · 入驻与服务须知</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200/60 flex items-center justify-center transition-all cursor-pointer"
            title="关闭 (Esc)"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* 协议正文内容区（滚动） */}
        <div
          ref={contentRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 text-xs text-foreground-700 leading-relaxed select-text"
        >
          {/* 特别提示 */}
          <div className="p-4 rounded-xl bg-amber-50/80 border border-amber-200/80 text-amber-900 space-y-2">
            <h3 className="font-bold text-sm text-amber-800 flex items-center gap-1.5">
              <i className="ri-alert-fill text-amber-600 text-base"></i>
              特别提示
            </h3>
            <p className="leading-relaxed">
              请您在申请入驻前认真阅读并充分理解本协议，尤其是关于平台服务边界、竞标规则、知识产权、违规处理和责任限制的条款。
            </p>
            <p className="leading-relaxed">
              平台为信息共享平台，不介入任何交易环节。您与客户的所有合作均为双方自愿行为，合作产生的一切法律后果由双方自行承担。请在合作前充分了解对方信息，审慎决策。平台向供给方提供的入驻、信息展示和需求竞标服务当前不收取费用。
            </p>
            <p className="leading-relaxed">
              本协议仅适用于平台供需服务模块的供给方入驻、档案展示和项目需求竞标。平台仅提供信息展示和供需撮合服务，不是您与需求发布方之间项目合作合同的相对方，也不负责见证、确认或管理双方合作。项目价格、交付、验收、付款、税费和争议处理，由供给方与需求发布方自行协商和承担。
            </p>
          </div>

          {/* 第一章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">一、协议主体与适用范围</h3>
            <p>1.1 本协议由广东倾云科技有限公司（以下简称“平台”）与申请入驻平台供需市场的供给方共同订立。</p>
            <p>1.2 供给方可以是：</p>
            <ul className="list-disc list-inside pl-2 space-y-1 text-foreground-600">
              <li>以自然人身份入驻的个人人才或个人创作者；</li>
              <li>以企业或其他依法登记主体身份入驻的企业服务商、制作团队或机构。</li>
            </ul>
            <p>1.3 本协议仅适用于供需服务模块中的供给方市场入驻、供给方档案和作品集展示、对企业项目需求提交竞标，以及与上述服务直接相关的审核、通知和账号管理。</p>
            <p>1.4 个人供给方应具备相应的民事行为能力；不具备完全民事行为能力的个人，应在取得法定代理人同意并依法承担相应责任的前提下使用本模块。企业供给方应确保其经办人具有合法授权。</p>
          </section>

          {/* 第二章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">二、相关定义</h3>
            <p>2.1 “需求发布方”是指在平台发布项目需求并有权查看该需求竞标信息的个人或企业主体。</p>
            <p>2.2 “供给方档案”是供给方在人才大厅或供需市场展示的业务资料，包括展示名称、简介、服务范围、服务方式、类目、城市、报价说明、作品集等公开信息。实名认证资料、企业登记资料和私密联系方式不属于公开档案内容。</p>
            <p>2.3 “需求竞标”是供给方针对正在招募的项目需求提交的合作意向。竞标可以包含报价、预计交付周期、服务说明和留言，但不当然构成平台或需求发布方已经接受的要约，也不代表项目合作合同已经成立。</p>
          </section>

          {/* 第三章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">三、入驻申请与市场资格</h3>
            <p>3.1 个人供给方入驻不以个人实名认证为前置条件，可以使用个人创作者或个人人才身份提交入驻资料和供给方档案。企业供给方入驻前应完成企业主体实名/认证核验，并按照平台要求提交企业名称、统一社会信用代码、营业执照、法定代表人及经办人授权等资料。平台按照公开的入驻规则进行基础审核；法律法规、监管要求或特定风险场景要求进一步核验身份的，平台可以另行向供给方说明核验范围和用途。</p>
            <p>3.2 供给方应根据自身真实主体申请入驻。个人不得冒用他人身份或以未经授权的企业名义入驻；企业经办人应取得合法、有效的授权，并对提交和维护的企业资料负责。</p>
            <p>3.3 只有市场入驻状态为“有效”且存在符合平台要求的已发布供给方档案时，供给方才可以参与项目需求竞标。入驻被暂停、下线、终止，或供给方档案被下架时，平台可以禁止新的竞标并隐藏相应公开资料。市场入驻有效不等于平台对供给方身份、资质、能力或履约能力作出认证或担保。</p>
            <p>3.4 平台有权根据业务范围、资料完整性、信息合理性、内容安全、知识产权风险和历史违规情况等因素审核入驻申请，并要求补充资料、修改档案或说明情况。平台不保证每一份入驻申请或供给方档案均会审核通过、持续展示或获得项目机会。</p>
            <p>3.5 供给方应及时更新主体资料、业务能力、作品集、报价说明和联系方式。因资料过期、错误、不完整或无法联系造成的后果，由供给方自行承担。</p>
            <p>3.6 供给方应妥善保管账号、密码及登录验证信息，不得出租、出借、出售或转让账号，不得允许他人冒用账号参与竞标。因供给方保管不善或主动授权他人使用账号造成的后果，由供给方自行承担；发现账号异常时，应及时通知平台并配合采取安全措施。</p>
          </section>

          {/* 第四章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">四、供给方档案与信息展示</h3>
            <p>4.1 供给方应保证档案中的展示名称、服务能力、工作年限、团队规模、案例、作品、报价和可用时间等信息真实、准确、合法，不得夸大、虚构、盗用或使用容易使他人误解的表述。</p>
            <p>4.2 供给方提交的头像、Logo、文字、图片、视频、文件和作品集等资料，应当符合平台的格式、审核和公开范围要求。平台可以对资料进行格式转换、压缩、裁切、脱敏、技术审核和安全处理。</p>
            <p>4.3 供给方同意平台在提供市场展示、搜索、推荐、审核、运营统计和平台宣传所必需的范围内，使用供给方提交的公开档案和作品资料。该使用为非独占、非转让、免许可费的使用授权，不改变作品权属，也不授权平台将作品用于与平台业务无关的商业用途。</p>
            <p>4.4 供给方将档案下线或停止入驻后，平台可以继续保留法律法规、争议处理、审计、风控和履行已发生服务所必需的记录；已经向需求发布方依法提供的资料，平台无法替供给方控制对方已经依法取得的信息。</p>
          </section>

          {/* 第五章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">五、需求竞标规则</h3>
            <p>5.1 供给方应根据需求详情、承接要求、预算模式、合作方式、交付周期和截止时间，自主判断是否竞标。供给方提交竞标时，应真实填写报价、预计周期、服务说明、承接条件和其他平台要求的信息。</p>
            <p>5.2 同一供给方对同一需求只能保留一条有效竞标。供给方可以在需求截止前修改报价、交付周期、服务说明和承接条件，每次修改以最新提交的内容为准。需求已截止或供给方已中标后，供给方不得通过平台单方修改竞标信息；实际合作条件需要调整的，由供给方与需求发布方自行协商确认。</p>
            <p>5.3 供给方可以在需求截止前且未中标时撤回竞标。撤回后，原竞标不再参与当前需求的竞标统计和筛选；如需求仍在招募且平台允许重新提交，供给方可以重新提交，但不得反复撤回、提交以扰乱竞标秩序。供给方已经中标或已经与需求发布方另行达成合作的，不适用本条的单方撤回规则。</p>
            <p>5.4 需求发布方可以查看供给方的公开档案和竞标信息，自主选择是否沟通及是否与供给方合作。需求发布方关闭需求、修改需求条件或选择其他供给方，不构成平台违约，也不代表供给方存在过错。</p>
            <p>5.5 平台对有效竞标仅展示“竞标中”和“已中标”两种业务状态。“竞标中”表示供给方的竞标仍在当前需求的招募范围内；“已中标”表示需求发布方已在平台将该供给方标记为中标。任何状态均不代表平台对项目达成、价格、交付、质量、付款或其他合作结果作出确认或担保。</p>
          </section>

          {/* 第六章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">六、双方沟通与项目合作</h3>
            <p>6.1 供给方与需求发布方应在正式合作前充分了解对方主体、项目内容、预算、交付标准、修改次数、验收方式、付款节点、违约责任、保密义务和知识产权归属，并自行协商合作条件。</p>
            <p>6.2 平台不参与供给方与需求发布方的报价、议价、合同签署、收款、付款、验收、交付、质量管理、发票开具或劳动关系安排，不负责见证或确认双方是否达成合作，不提供资金托管、先行赔付或项目结果担保。</p>
            <p>6.3 供给方与需求发布方之间形成的合作关系属于双方独立的民事法律关系。双方因项目质量、延期、取消、付款、退款、劳动用工、商业秘密或知识产权产生的争议，由双方自行协商处理；平台不替代双方承担合同责任或作出实体裁判。</p>
            <p>6.4 供给方不得利用平台信息从事与目标需求无关的骚扰、群发营销、诱导转账、恶意拉客、出售或转交他人信息等行为，也不得以平台名义向需求发布方作出未经授权的承诺。</p>
            <p>6.5 供给方在竞标、沟通或合作过程中知悉的需求发布方未公开资料、商业计划、客户信息、源文件、报价和其他保密信息，应仅限于本次需求沟通或合作使用，不得擅自复制、公开、转让或用于其他项目。保密范围和期限以双方另行约定为准；双方未作约定的，供给方仍应按照法律法规和诚实信用原则采取合理保密措施。</p>
          </section>

          {/* 第七章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">七、知识产权与内容合规</h3>
            <p>7.1 供给方保证其提交、展示、发送或交付的文字、图片、视频、音频、脚本、模型生成内容、软件、字体、音乐、肖像和其他材料具有合法来源，或已取得足以覆盖平台展示、审核、竞标沟通和项目使用范围的授权。</p>
            <p>7.2 供给方不得通过平台发布或提交以下内容：</p>
            <ul className="list-disc list-inside pl-2 space-y-1 text-foreground-600">
              <li>违反法律法规、监管要求、公序良俗或平台规则的内容；</li>
              <li>侵害他人著作权、商标权、专利权、肖像权、名誉权、隐私权、商业秘密或其他合法权益的内容；</li>
              <li>虚假宣传、冒用身份、伪造证书、盗用案例、刷量、抄袭或恶意诋毁他人的内容；</li>
              <li>含有违法、淫秽、暴力、赌博、诈骗、极端、侵害未成年人或其他平台禁止内容的素材；</li>
              <li>可能危害网络安全、传播恶意程序、窃取信息或绕过平台审核的文件和链接。</li>
            </ul>
            <p>7.3 如需求发布方、权利人、监管部门或其他主体就供给方资料提出权利主张或投诉，供给方应在平台通知的期限内提供授权链、来源证明、修改记录、下架说明或其他必要材料，并自行承担因其资料或行为引发的法律责任和处理成本。</p>
            <p>7.4 因供给方提交或使用的资料导致平台遭受索赔、处罚、损失或合理支出，供给方应依法承担相应责任；但平台存在法定过错的，依法律规定承担相应责任。</p>
          </section>

          {/* 第八章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">八、个人信息与隐私</h3>
            <p>8.1 供给方提交的手机号、邮箱、微信及其他联系方式属于非公开或敏感信息。个人供给方入驻不要求提交身份证等个人实名材料；企业供给方应提交企业主体实名/认证核验所需的营业执照、统一社会信用代码、法定代表人及经办人授权等材料。上述企业认证材料仅用于主体核验、风险控制、平台管理和依法留存，不作为公开档案内容。平台将按照法律法规、业务必要和授权范围处理供给方主动提交的信息，并采取必要的保护措施。</p>
            <p>8.2 供给方应确保其提交的平台资料和联系方式属于本人或已取得相关人员的合法授权。企业供给方应确保联系人、经办人和团队成员已知悉并同意相关处理安排。</p>
            <p>8.3 供给方不得收集、买卖、交换、公开或非法使用其他用户的身份证件、联系方式、简历、作品源文件或其他个人信息。确因项目沟通需要处理相关信息的，应遵守适用法律并采取必要的保密和安全措施。</p>
          </section>

          {/* 第九章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">九、费用、权益与税费</h3>
            <p>9.1 平台当前向供给方提供的供需服务模块内的入驻、供给方档案展示和需求竞标服务不收费。平台不会以本协议为依据向供给方收取入驻费、竞标费、项目保证金、项目佣金或其他服务费用。平台未来如提供独立的收费增值服务，将另行明确服务内容和收费标准，并由供给方自主选择、单独确认。</p>
            <p>9.2 供给方与需求发布方之间的项目报价、服务费、付款和退款，由双方另行约定。平台不代收代付项目款项、不参与项目交易，也不因项目未成交、未付款或合作中止向任何一方承担垫付责任。</p>
            <p>9.3 供给方应依法承担因入驻、竞标、项目合作、收款、开票和取得收入产生的税费及申报义务。企业供给方还应自行负责其员工、外包人员和合作团队的用工、薪酬、社保及相关合规事项。</p>
          </section>

          {/* 第十章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">十、平台管理措施与责任边界</h3>
            <p>10.1 平台可以基于审核结果、投诉举报、风险识别、异常行为、监管要求或业务安全需要，对供给方采取提示整改、要求补充资料、限制竞标、隐藏档案、暂停入驻、下架内容、终止服务或注销入驻等措施。</p>
            <p>10.2 平台按照违规行为的性质、次数、影响范围和纠正情况分级处理：</p>
            <ul className="list-disc list-inside pl-2 space-y-1 text-foreground-600">
              <li>一般违规，如资料缺失、描述不清、竞标信息填写错误或首次轻微扰乱竞标秩序，平台可以提示整改、要求限期修改；逾期未改的，可以暂时隐藏档案或限制竞标。</li>
              <li>较重违规，如多次虚假宣传、恶意低价后临时抬价、反复撤回和提交竞标、骚扰或超范围营销、未经授权使用他人信息，平台可以下架相关内容、暂停竞标 7 至 30 日；重复发生的，可以暂停入驻资格。</li>
              <li>严重违规，如冒用企业或他人身份、诈骗或诱导转账、严重侵犯知识产权、泄露个人信息、发布违法内容或绕过平台限制再次违规，平台可以立即下架相关内容、暂停或终止入驻；涉嫌违法的，平台将依法配合处理。</li>
            </ul>
            <p>10.3 账号关联处理应以客观、可核验的证据为基础。平台不会仅因同一设备、网络或办公地点而直接认定多个账号属于同一违规主体；但对已确认存在冒用身份、批量注册规避处理或其他关联违规的账号，平台可以在必要范围内采取限制措施。</p>
            <p>10.4 供给方收到处理通知后 7 日内可以通过平台指定入口提交一次申诉。申诉应包含相关需求或竞标编号、事实说明、发生时间等信息；如有聊天记录、文件来源、授权证明、付款或交付凭证等材料，也可以一并提交。平台原则上在收到完整申诉材料后 7 个工作日内反馈处理结果；事项复杂的，平台可以说明原因后延长处理时间。</p>
            <p>10.5 投诉人应提交可核验的具体对象、发生时间、行为描述及截图、聊天记录、文件元数据、合同或付款凭证等材料。平台可以不采纳证据不足、明显失实、恶意投诉或伪造证据的投诉；恶意投诉或伪造证据的，平台可以按照本条采取相应措施。</p>
            <p>10.6 平台会尽合理努力维护服务的连续性和安全性，但网络、设备、通信、第三方服务、系统维护、不可抗力或其他平台无法合理控制的原因，可能导致服务中断、数据延迟或部分功能不可用。平台将根据实际情况采取恢复、通知或补救措施。</p>
            <p>10.7 在法律允许的范围内，平台不对需求信息的真实性、需求发布方的履约能力、竞标结果、合作机会、项目收益或双方合作结果作出明示或默示保证。平台不排除或限制法律规定不得排除或限制的责任。</p>
          </section>

          {/* 第十一章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">十一、协议变更、暂停与终止</h3>
            <p>11.1 平台可以根据法律法规、监管要求、业务调整或风险管理需要修订本协议。涉及供给方主要权利义务的重大变更，平台将通过页面公告、站内通知或其他合理方式提示；供给方继续使用相关服务，或按页面提示重新确认后，视为接受修订内容。</p>
            <p>11.2 供给方可以申请停止入驻或关闭公开档案。停止入驻不影响已经产生的竞标记录、已授权的联系方式、依法应保存的资料以及供给方与需求发布方已经独立成立的合作关系。</p>
            <p>11.3 出现法律法规要求、平台服务终止、供给方严重违约或其他无法继续履行的情形时，本协议可以终止。协议终止后，保密、知识产权、个人信息保护、责任承担和依法应持续履行的条款仍然有效。</p>
          </section>

          {/* 第十二章 */}
          <section className="space-y-2.5 border-t border-gray-100 pt-4">
            <h3 className="font-bold text-sm text-foreground-950 font-heading">十二、其他</h3>
            <p>12.1 本协议标题仅为阅读便利，不影响条款含义。部分条款被认定无效或不可执行的，不影响其他条款的效力。</p>
            <p>12.2 平台在供需服务模块页面依法公布的使用提示、审核要求和操作规则，是本协议的补充。涉及供给方主要权利义务的内容，平台应在生效前以显著方式提示；补充规则与本协议不一致的，以对供给方更有利且不违反法律法规的约定为准，法律法规另有规定或供给方另行确认的除外。</p>
            <p>12.3 本协议未尽事宜，按照中华人民共和国现行法律法规及平台依法发布的规则执行。</p>
            <p>12.4 供给方通过页面勾选、点击确认或以其他方式提交入驻申请，即视为已经阅读、理解并同意接受本协议；平台保存相应的协议版本、确认时间和操作记录，仅用于证明供给方接受本协议及平台管理需要。</p>
          </section>

          {/* 附言提示 */}
          <div className="pt-4 border-t border-gray-100 text-[11px] text-foreground-400 italic">
            本协议为供需服务模块的 AI创作服务供给方入驻及需求竞标服务协议_草案。正式发布前，请将第 1.1 条中的【公司全称】替换为实际运营公司的营业执照全称，并由平台运营及法务根据实际业务规则审核确认。
          </div>
        </div>

        {/* 底部按钮栏 */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
          <p className="text-[11px] text-foreground-400 flex items-center gap-1">
            {!hasScrolledToBottom ? (
              <span className="text-amber-600 font-medium flex items-center gap-1">
                <i className="ri-arrow-down-circle-line text-sm"></i>
                请向下滑动阅读完整协议条款后同意
              </span>
            ) : (
              <span className="text-emerald-600 font-medium flex items-center gap-1">
                <i className="ri-checkbox-circle-fill text-sm"></i>
                已完成全部协议阅读
              </span>
            )}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-100 transition-all cursor-pointer"
            >
              关闭
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!hasScrolledToBottom}
              className={`px-5 py-2 rounded-xl text-xs font-medium transition-all shadow-xs flex items-center gap-1.5 ${
                hasScrolledToBottom
                  ? 'bg-primary-600 hover:bg-primary-700 text-white cursor-pointer'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-80'
              }`}
            >
              <i className="ri-checkbox-circle-line text-sm"></i>
              {hasScrolledToBottom ? '已阅读并同意' : '请滑动到底部阅读'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
